import { SEARCH_SUGGESTIONS_CACHE_TTL, SEARCH_SUGGESTIONS_SERVICE_URL } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { http } from '~/modules/utilities/http';
import { TrieSearchItem } from './trieService';

const STORAGE_KEY = 'searchSuggestions_cache';

interface CachedData {
    dataset: TrieSearchItem[];
    expiry: number;
}

function readCache(): CachedData | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (parsed && Array.isArray(parsed.dataset) && typeof parsed.expiry === 'number') {
            return parsed as CachedData;
        }
        return null;
    } catch {
        return null;
    }
}

function writeCache(dataset: TrieSearchItem[]): void {
    try {
        const cache: CachedData = {
            dataset,
            expiry: Date.now() + SEARCH_SUGGESTIONS_CACHE_TTL * 60 * 60 * 1000, // convert hours to ms
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch {
        /* ignore quota/security errors */
    }
}

function isCacheValid(cache: CachedData): boolean {
    return Date.now() < cache.expiry;
}

async function fetchFromBackend(): Promise<TrieSearchItem[]> {
    const config = FeatureFlags.FF_ACE_SUGGESTION_COMPRESSION ? { headers: { 'Accept-Encoding': 'gzip' } } : {};
    const response = await http.get(`${SEARCH_SUGGESTIONS_SERVICE_URL}/v1/suggestions/bootstrap`, config);
    return response.data.keyword.map((suggestion: string) => ({ keyword: suggestion }));
}

export async function getSearchSuggestionData(): Promise<{ data: TrieSearchItem[]; fromCache: boolean }> {
    const cache = readCache();

    if (cache && isCacheValid(cache)) {
        return { data: cache.dataset, fromCache: true };
    }

    const freshData = await fetchFromBackend();
    writeCache(freshData);
    return { data: freshData, fromCache: false };
}

export function clearSearchSuggestionCache(): void {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
}
